import Papa from 'papaparse';
import type { AffiliateCsvImportRowErrorCode } from '@fluenceur/types';
import { isValidEmail } from '@/lib/is-valid-email';
import {
  detectHeaderMapping,
  getMissingRequiredFields,
  type CsvField,
  type HeaderMapping,
} from './header-aliases';

export const MAX_ROWS = 2000;
// Parsing a multi-megabyte CSV in the browser makes the import modal unresponsive
// long before the row guard can help. Keep this deliberately modest; the server
// remains the authority for payload validation.
export const MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024;

/** The subset of the shared row-error contract the browser can decide on its own. */
export type ParseErrorCode = Extract<
  AffiliateCsvImportRowErrorCode,
  | 'invalid_email'
  | 'missing_email'
  | 'missing_first_name'
  | 'duplicate_in_file'
  | 'conflicting_duplicate'
>;

export interface ParsedRow {
  rowNumber: number;
  email: string;
  firstName: string;
  shopifyCode: string | null;
  errors: ParseErrorCode[];
}

export interface CsvParseResult {
  headers: string[];
  mapping: HeaderMapping;
  missingRequired: CsvField[];
  /** Kept only for the lifetime of the wizard so manual mapping can rebuild rows. */
  rawRows: Record<string, unknown>[];
  rows: ParsedRow[];
  totalRows: number;
  exceededLimit: boolean;
  /** The file carried no header row; its first line is a contact, not labels. */
  isHeaderless: boolean;
  /**
   * What to show for each column of {@link CsvParseResult.headers}. Identical to
   * `headers` for a labelled file; a sample value for a headerless one, whose
   * headers are synthetic keys the brand would not recognize.
   */
  headerLabels: string[];
}

const cleanCell = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

const readBlobAsText = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('Unable to read CSV header'));
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.readAsText(blob);
  });

const parseHeaderOnlyFallback = async (file: File): Promise<string[]> => {
  // `step` does not run for a CSV containing only its header. Read only a small
  // prefix for that empty-state case; normal and oversized files still stream
  // through Papa and abort at row 2,001.
  const firstChunk = await readBlobAsText(file.slice(0, 64 * 1024));
  const result = Papa.parse<string[]>(firstChunk, { preview: 1, skipEmptyLines: 'greedy' });
  return (result.data[0] ?? []).map((header) => header.trim());
};

/**
 * Whether Papa's "header" row is really the first contact.
 *
 * Papa is always given `header: true`, so a file exported without labels loses
 * its first line: it becomes the column names and never reaches the recipient
 * list. A cell holding a complete email address is the evidence, because no
 * header row labels a column with someone's address. An alias match elsewhere
 * on the line does not overrule it — a discount cell reading "CODE" matches the
 * optional code alias while the line is plainly a contact. Only a first line
 * already doing a header's whole job, naming both required columns, is trusted
 * as one.
 */
export const looksLikeDataRow = (headers: readonly string[]): boolean => {
  if (headers.length === 0) return false;
  if (!headers.some((header) => isValidEmail(header.toLowerCase()))) return false;
  return getMissingRequiredFields(detectHeaderMapping(headers)).length > 0;
};

/** Column key for a file with no labels of its own. Stable, unique, non-empty. */
const headerlessColumnKey = (index: number): string => `column_${index + 1}`;

interface HeaderlessParse {
  rawRows: Record<string, unknown>[];
  keys: string[];
  /** First non-empty value per column, so the mapping step stays choosable. */
  samples: string[];
  exceededLimit: boolean;
}

/**
 * Re-read a file whose first line is a contact, this time without a header row.
 *
 * Worth the second pass: keying rows on the first line's own values would break
 * on an empty cell (an empty key reads as "unmapped" in the mapping step, so
 * that column could never be chosen) and would silently merge two columns that
 * happen to hold the same value.
 */
const parseHeaderlessFile = (file: File): Promise<HeaderlessParse> =>
  new Promise((resolve, reject) => {
    const matrix: string[][] = [];
    let exceededLimit = false;

    Papa.parse<string[]>(file, {
      header: false,
      skipEmptyLines: 'greedy',
      worker: canParseOffMainThread(),
      step: (result, parser) => {
        if (matrix.length >= MAX_ROWS) {
          exceededLimit = true;
          parser.abort();
          return;
        }
        matrix.push(result.data);
      },
      complete: () => {
        const columnCount = matrix.reduce((widest, cells) => Math.max(widest, cells.length), 0);
        const keys = Array.from({ length: columnCount }, (_, i) => headerlessColumnKey(i));
        resolve({
          keys,
          samples: keys.map(
            (_, i) => matrix.find((cells) => cleanCell(cells[i]).length > 0)?.[i]?.trim() ?? ''
          ),
          rawRows: matrix.map((cells) =>
            Object.fromEntries(keys.map((key, i) => [key, cells[i] ?? '']))
          ),
          exceededLimit,
        });
      },
      error: (err) => reject(err),
    });
  });

const buildRow = (
  raw: Record<string, unknown>,
  mapping: HeaderMapping,
  rowNumber: number
): ParsedRow => {
  const email = cleanCell(mapping.email ? raw[mapping.email] : '').toLowerCase();
  const firstName = cleanCell(mapping.firstName ? raw[mapping.firstName] : '');
  const shopifyCodeRaw = cleanCell(mapping.shopifyCode ? raw[mapping.shopifyCode] : '');

  const errors: ParseErrorCode[] = [];
  if (!email) {
    errors.push('missing_email');
  } else if (!isValidEmail(email)) {
    errors.push('invalid_email');
  }
  if (!firstName) {
    errors.push('missing_first_name');
  }

  return {
    rowNumber,
    email,
    firstName,
    shopifyCode: shopifyCodeRaw ? shopifyCodeRaw : null,
    errors,
  };
};

const flagDuplicates = (rows: ParsedRow[]): ParsedRow[] => {
  const rowsByEmail = new Map<string, ParsedRow[]>();
  for (const row of rows) {
    if (!row.email || row.errors.includes('invalid_email')) continue;
    const bucket = rowsByEmail.get(row.email);
    if (bucket) {
      bucket.push(row);
    } else {
      rowsByEmail.set(row.email, [row]);
    }
  }

  const conflictingEmails = new Set(
    [...rowsByEmail.entries()]
      .filter(([, matchingRows]) => {
        if (matchingRows.length < 2) return false;
        const payloads = new Set(
          matchingRows.map((row) => `${row.firstName}\u0000${row.shopifyCode ?? ''}`)
        );
        return payloads.size > 1;
      })
      .map(([email]) => email)
  );

  const seen = new Set<string>();
  return rows.map((row) => {
    if (!row.email || row.errors.includes('invalid_email')) {
      return row;
    }
    if (conflictingEmails.has(row.email)) {
      return { ...row, errors: [...row.errors, 'conflicting_duplicate'] };
    }
    if (seen.has(row.email)) {
      return { ...row, errors: [...row.errors, 'duplicate_in_file'] };
    }
    seen.add(row.email);
    return row;
  });
};

/**
 * Rebuild validated rows after the user changes the header mapping.
 *
 * `rowNumber` is the line the brand will look for in their own file, so it
 * counts from 2 when line 1 held the labels and from 1 when the file carried no
 * header and its first line is a contact.
 */
export const buildRowsFromMapping = (
  rawRows: readonly Record<string, unknown>[],
  mapping: HeaderMapping,
  isHeaderless = false
): ParsedRow[] =>
  flagDuplicates(
    rawRows.map((raw, idx) => buildRow(raw, mapping, idx + (isHeaderless ? 1 : 2)))
  );

/**
 * Papa spawns its worker from an inline blob, so no bundler wiring is needed.
 * Feature-detected because jsdom (vitest) exposes no `Worker`: Papa would fall
 * back on its own, but the explicit check keeps the test path unambiguous.
 */
const canParseOffMainThread = (): boolean => typeof Worker !== 'undefined';

/**
 * Worker mode posts the parse config to the Worker, so the config must stay
 * structured-cloneable: any function option other than `step`/`complete`
 * (which Papa wires up itself, without cloning) throws a DataCloneError.
 * Header trimming therefore happens here, on the rows received back on the
 * main thread, never via `transformHeader`.
 */
const trimRowKeys = (row: Record<string, unknown>): Record<string, unknown> => {
  let needsTrim = false;
  for (const key in row) {
    if (key !== key.trim()) {
      needsTrim = true;
      break;
    }
  }
  if (!needsTrim) return row;
  const normalized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(row)) {
    normalized[key.trim()] = value;
  }
  return normalized;
};

export const parseCsvFile = (file: File): Promise<CsvParseResult> =>
  new Promise((resolve, reject) => {
    const rawRows: Record<string, unknown>[] = [];
    let headers: string[] = [];
    let exceededLimit = false;

    Papa.parse<Record<string, unknown>>(file, {
      header: true,
      skipEmptyLines: 'greedy',
      // Keeps the import modal responsive on a 2,000-row file: parsing a
      // multi-megabyte CSV on the main thread freezes the whole dashboard.
      worker: canParseOffMainThread(),
      // Read incrementally so a file over the 2,000-row product limit does not
      // need to be fully parsed before we can reject it. Papa still gives us the
      // detected headers and the first 2,000 raw rows for the UI error state.
      step: (result, parser) => {
        if (headers.length === 0) {
          headers = (result.meta.fields ?? []).map((header) => header.trim());
        }

        if (rawRows.length >= MAX_ROWS) {
          exceededLimit = true;
          parser.abort();
          return;
        }

        rawRows.push(trimRowKeys(result.data));
      },
      complete: (result) => {
        void (async () => {
          try {
            // `step` is never called for a headers-only CSV, but Papa exposes the
            // columns on complete so mapping still works for that empty-state UI.
            // In worker mode Papa deletes the per-chunk results before calling
            // `complete`, so the argument itself can be undefined: read through.
            if (headers.length === 0) {
              headers = (result?.meta?.fields ?? []).map((header) => header.trim());
            }
            if (headers.length === 0 && file.size > 0) {
              headers = await parseHeaderOnlyFallback(file);
            }
            const isHeaderless = looksLikeDataRow(headers);
            // The header-mode pass consumed the first contact as column names,
            // so a headerless file is read again from the top under synthetic
            // keys, which also re-counts it against the row limit.
            const headerless = isHeaderless ? await parseHeaderlessFile(file) : null;
            const effectiveHeaders = headerless?.keys ?? headers;
            const effectiveRows = headerless?.rawRows ?? rawRows;
            const overLimit = headerless?.exceededLimit ?? exceededLimit;
            const mapping = detectHeaderMapping(effectiveHeaders);
            const missingRequired = getMissingRequiredFields(mapping);
            // An aborted parser has observed row 2,001 but intentionally stops
            // there. Preserve that lower bound instead of pretending to know the
            // full file length (which would require parsing the remainder).
            const totalRows = overLimit ? MAX_ROWS + 1 : effectiveRows.length;
            const rows = buildRowsFromMapping(effectiveRows, mapping, isHeaderless);

            resolve({
              headers: effectiveHeaders,
              headerLabels: headerless?.samples ?? headers,
              mapping,
              missingRequired,
              rawRows: effectiveRows,
              rows,
              totalRows,
              exceededLimit: overLimit,
              isHeaderless,
            });
          } catch (err) {
            reject(err);
          }
        })();
      },
      error: (err) => reject(err),
    });
  });

export const computeCounters = (rows: readonly ParsedRow[]) => {
  let valid = 0;
  let errors = 0;
  let duplicates = 0;
  for (const row of rows) {
    if (row.errors.length === 0) {
      valid += 1;
      continue;
    }
    // Error-wins tie-breaker: a row flagged BOTH as duplicate and as having a per-row
    // problem (missing email, invalid email, missing first name) is counted as an error.
    // Today flagDuplicates only marks rows with valid emails so the combo cannot occur,
    // but if that invariant ever loosens, "this row is broken" is the more actionable
    // framing for the brand than "this row is a duplicate".
    const hasOtherError = row.errors.some((code) => code !== 'duplicate_in_file');
    if (hasOtherError) {
      errors += 1;
    } else {
      duplicates += 1;
    }
  }
  return { valid, errors, duplicates };
};
